import { useEffect, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import Shell from "@/components/Shell";
import PhotoUpload from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { LayoutDashboard, Store, CreditCard, MapPin, Loader2, Send, CheckCircle2, Clock, XCircle, Utensils, FileText } from "lucide-react";
import { toast } from "sonner";
import AdvertiserBilling from "@/components/advertiser/AdvertiserBilling";
import AdvertiserContentProfile from "@/components/advertiser/AdvertiserContentProfile";
import LocationPicker from "@/components/shop/LocationPicker";

const BUSINESS_TYPES = ["Restaurant", "Brewery", "Ice Cream", "Café", "Gift Shop", "Museum", "Bakery", "Bar", "Hotel", "Other"];

const PLAN_META = {
  advertiser_annual: { price: "$99", per: "/year", billed: "Billed yearly per location", renew: "yearly" },
};

const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30", icon: Clock },
  pending: { label: "Pending approval", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30", icon: Clock },
  approved: { label: "Approved · Sponsored Pin", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-400 border-red-500/30", icon: XCircle },
  none: { label: "No listing", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30", icon: Clock },
};

function PinPreview({ listing, categories, logoId }) {
  const catName = categories.find((c) => c.id === listing.category_id)?.name || listing.category_name || "Category";
  return (
    <div className="p-5 rounded-xl border border-emerald-500/30 bg-gradient-to-b from-slate-900 to-slate-950 shadow-xl" data-testid="advertiser-pin-preview">
      <div className="text-xs font-mono uppercase tracking-widest text-primary mb-3">Rider App pin preview</div>
      <div className="relative">
        <div className="rounded-xl overflow-hidden border border-border bg-secondary">
          {listing.photos?.[0] ? (
            <img src={fileUrl(listing.photos[0])} alt="preview" className="w-full h-36 object-cover" />
          ) : (
            <div className="w-full h-36 flex items-center justify-center text-muted-foreground"><Utensils className="w-8 h-8" /></div>
          )}
          <div className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0 overflow-hidden">
                {logoId ? (
                  <img src={fileUrl(logoId)} alt="shop" className="w-full h-full object-cover" data-testid="pin-shop-logo" />
                ) : (
                  <MapPin className="w-4 h-4 text-primary-foreground" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-sm truncate">{listing.business_name || "Your business"}</div>
                <div className="text-xs text-muted-foreground truncate">{catName} · {listing.business_type || "Type"}</div>
              </div>
            </div>
          </div>
        </div>
        <Badge className="absolute -top-2 -right-2 bg-amber-500 text-black hover:bg-amber-500">Sponsored</Badge>
      </div>
    </div>
  );
}

export default function AdvertiserDashboard() {
  const [tab, setTab] = useState("listing");
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [riders, setRiders] = useState("");
  const [pin, setPin] = useState({ lat: null, lng: null });

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/my/listing"), api.get("/categories")]);
    setData(l.data);
    setCategories(c.data);
    setRiders(l.data.advertiser?.estimated_yearly_riders != null ? String(l.data.advertiser.estimated_yearly_riders) : (l.data.tenant?.estimated_yearly_riders != null ? String(l.data.tenant.estimated_yearly_riders) : ""));
    setPin({ lat: l.data.advertiser?.latitude ?? null, lng: l.data.advertiser?.longitude ?? null });
    setForm(
      l.data.listing || {
        business_type: "", category_id: "", business_name: l.data.advertiser?.business_name || "",
        address: "", hours: "", website: "", menu_link: "", description: "", photos: [],
      }
    );
  };

  useEffect(() => { load(); }, []);

  const savePin = async (lat, lng) => {
    const prev = pin;
    setPin({ lat, lng });
    try {
      const { data } = await api.put("/advertiser/location", { latitude: lat, longitude: lng });
      if (data.requeued_for_approval) {
        toast.success(`Location moved to ${data.partner_shop_name}'s territory (${data.zone_name}). Your listing was sent to ${data.partner_shop_name} for approval before it goes live again.`);
      } else if (data.partner_changed) {
        toast.success(`Location updated — now attributed to ${data.partner_shop_name} (${data.zone_name}).`);
      } else {
        toast.success(`Map location updated · ${data.zone_name}`);
      }
      load();
    } catch (e) {
      setPin(prev); // reject → retain previous valid location
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.category_name = categories.find((c) => c.id === form.category_id)?.name || "";
      await api.put("/my/listing", payload);
      toast.success("Listing saved");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    try {
      await api.post("/my/listing/submit");
      toast.success("Submitted to bike rental shop for approval");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const changePlan = async (plan) => {
    try {
      await api.post("/advertiser/plan", { plan });
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const changePlanActive = async (plan) => {
    try {
      const { data: res } = await api.post("/my/subscription/change-plan", { plan });
      const when = res.effective_at ? new Date(res.effective_at).toLocaleDateString() : "your next renewal";
      toast.success(`Switching to ${PLAN_META[plan].billed.toLowerCase()} — new rate starts ${when}`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addCard = async () => {
    setSubLoading(true);
    try {
      const { data: res } = await api.post("/advertiser/setup-checkout", { origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setSubLoading(false);
    }
  };

  const cancelSub = async () => {
    try {
      const { data: res } = await api.post("/my/subscription/cancel");
      const until = res.effective_until ? new Date(res.effective_until).toLocaleDateString() : "the end of your term";
      toast.success(`Cancellation scheduled — active until ${until}`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const reactivateSub = async () => {
    try {
      await api.post("/my/subscription/reactivate");
      toast.success("Subscription reactivated — your renewal date is unchanged");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveRiders = async () => {
    try {
      await api.put("/advertiser/rider-count", { estimated_yearly_riders: parseInt(riders) || 0 });
      toast.success("Rider estimate saved");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const nav = [
    { key: "listing", label: "My Listing", icon: Store },
    { key: "contentprofile", label: "Public Listing", icon: FileText },
    { key: "preview", label: "Pin Preview", icon: MapPin },
    { key: "billing", label: "Subscription", icon: CreditCard },
    { key: "invoices", label: "Billing", icon: FileText },
  ];

  if (!data || !form)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const status = data.listing?.status || "none";
  const st = STATUS[status];
  const subActive = data.advertiser?.subscription_status === "active";
  const cardSaved = !!data.advertiser?.payment_method_saved;
  const ridersNum = parseInt(riders) > 0 ? parseInt(riders) : 4000;

  return (
    <Shell nav={nav} active={tab} onNavigate={setTab} title="Advertiser Dashboard" badge="Advertiser">
      {tab === "invoices" && <AdvertiserBilling />}
      {tab === "contentprofile" && <AdvertiserContentProfile />}
      {tab === "listing" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Badge className={`${st.cls} border`} data-testid="listing-status-badge"><st.icon className="w-3 h-3 mr-1" />{st.label}</Badge>
              <div className="flex gap-2">
                <Button variant="outline" onClick={save} disabled={saving} data-testid="listing-save-button">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
                </Button>
                <Button onClick={submit} disabled={!cardSaved || !data.listing} data-testid="listing-submit-button">
                  <Send className="w-4 h-4 mr-2" />Submit for approval
                </Button>
              </div>
            </div>
            {!cardSaved && <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">Add a payment method before submitting. Your card is only charged once your bike rental shop approves your listing.</div>}

            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <h3 className="font-heading font-bold">Business details</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Business type</Label>
                  <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                    <SelectTrigger data-testid="listing-business-type" className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                    <SelectTrigger data-testid="listing-category-select" className="mt-1.5"><SelectValue placeholder="Shop category" /></SelectTrigger>
                    <SelectContent>
                      {categories.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Shop hasn't added categories</div>}
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Business name</Label>
                <Input value={form.business_name} onChange={set("business_name")} data-testid="listing-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={set("address")} data-testid="listing-address-input" className="mt-1.5" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Hours</Label>
                  <Input value={form.hours} onChange={set("hours")} placeholder="Mon–Sun 8am–6pm" data-testid="listing-hours-input" className="mt-1.5" />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input value={form.website} onChange={set("website")} data-testid="listing-website-input" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Menu link</Label>
                <Input value={form.menu_link} onChange={set("menu_link")} data-testid="listing-menu-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Short description</Label>
                <Textarea value={form.description} onChange={set("description")} data-testid="listing-description-input" className="mt-1.5" rows={3} />
              </div>
              <div>
                <Label className="mb-2 block">Photos</Label>
                <PhotoUpload photos={form.photos} onChange={(photos) => setForm({ ...form, photos })} testId="listing-photos" />
              </div>
              <div>
                <Label className="mb-1 block">Map location</Label>
                <p className="text-xs text-muted-foreground mb-2">Click the map or drag the pin to set exactly where your Sponsored Pin appears for riders. Changes save automatically.</p>
                <LocationPicker lat={pin.lat} lng={pin.lng} onChange={savePin} zones={data.zones || []} boundary={data.boundary || []} height={280} />
              </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-24 h-fit">
            <PinPreview listing={form} categories={categories} logoId={data.tenant?.logo_file_id} />
          </div>
        </div>
      )}

      {tab === "preview" && (
        <div className="max-w-sm"><PinPreview listing={form} categories={categories} logoId={data.tenant?.logo_file_id} /></div>
      )}

      {tab === "billing" && (
        <div className="max-w-lg space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold">Advertiser Program</h3>
              <Badge className={subActive ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-slate-500/15 text-slate-300 border border-slate-500/30"} data-testid="subscription-status-badge">
                {data.advertiser?.subscription_status || "none"}
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-2 mb-2" data-testid="plan-chooser">
              {["advertiser_annual"].map((k) => {
                const current = (data.advertiser?.plan || "advertiser_annual") === k;
                return (
                  <button key={k} type="button" disabled={current} onClick={() => (subActive ? changePlanActive(k) : changePlan(k))} data-testid={`plan-option-${k}`}
                    className={`relative rounded-lg border p-3 text-left transition-colors disabled:cursor-default ${current ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
                    {PLAN_META[k].badge && <span className="absolute -top-2 right-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground" data-testid="annual-savings-badge">{PLAN_META[k].badge}</span>}
                    <div className="font-heading font-bold">{PLAN_META[k].price}<span className="text-xs text-muted-foreground font-normal">{PLAN_META[k].per}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">{PLAN_META[k].billed}{PLAN_META[k].note ? ` · ${PLAN_META[k].note}` : ""}</div>
                    {current && <div className="text-[10px] text-primary mt-1 font-medium">Current plan</div>}
                  </button>
                );
              })}
            </div>
            {subActive
              ? <p className="text-xs text-muted-foreground mb-4" data-testid="switch-plan-hint">Switch anytime — your new rate takes effect at your next renewal.</p>
              : <div className="mb-4" />}
            <div className="text-4xl font-heading font-extrabold" data-testid="plan-price">{PLAN_META[data.advertiser?.plan || "advertiser_annual"].price}<span className="text-base text-muted-foreground font-normal">{PLAN_META[data.advertiser?.plan || "advertiser_annual"].per}</span></div>
            {subActive && data.advertiser?.subscription_current_period_end && (
              <p className="text-sm text-muted-foreground mt-2" data-testid="next-renewal-date">Next renewal: {new Date(data.advertiser.subscription_current_period_end).toLocaleDateString()}</p>
            )}
            <ul className="text-sm text-muted-foreground mt-4 space-y-2">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" />Sponsored pin on rider routes</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" />Photos, hours, menu & website</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" />Automatic {PLAN_META[data.advertiser?.plan || "advertiser_annual"].renew} renewal</li>
            </ul>
            {subActive ? (
              data.advertiser?.cancel_at_period_end ? (
                <Button className="w-full mt-6" onClick={reactivateSub} data-testid="reactivate-subscription-button">Reactivate subscription</Button>
              ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full mt-6" data-testid="cancel-subscription-button">Cancel subscription</Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="cancel-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your program?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your cancellation takes effect at the end of your current paid period. You'll keep your Sponsored Pin until{" "}
                      <strong className="text-foreground" data-testid="cancel-effective-until">{data.advertiser?.subscription_current_period_end ? new Date(data.advertiser.subscription_current_period_end).toLocaleDateString() : "the end of your term"}</strong>{" "}
                      and won't be charged again.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-dialog-dismiss">Keep my program</AlertDialogCancel>
                    <AlertDialogAction onClick={cancelSub} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="cancel-dialog-confirm">Confirm cancellation</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )
            ) : cardSaved ? (
              <div className="mt-6 text-sm text-primary bg-primary/10 border border-primary/20 rounded-lg p-3" data-testid="billing-info">
                Your payment method is saved. Your subscription starts automatically the moment your bike rental shop approves your listing — no charge until then.
              </div>
            ) : (
              <Button className="w-full mt-6" onClick={addCard} disabled={subLoading} data-testid="add-card-button">
                {subLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Add payment method
              </Button>
            )}
            {subActive && data.advertiser?.cancel_at_period_end && (
              <p className="text-xs text-amber-400 mt-2" data-testid="cancel-scheduled-note">Cancellation scheduled — your program stays active until {data.advertiser?.subscription_current_period_end ? new Date(data.advertiser.subscription_current_period_end).toLocaleDateString() : "the end of your term"}.</p>
            )}
            <p className="text-xs text-muted-foreground mt-3" data-testid="pricing-change-note">Pricing may change annually based on market conditions.</p>
            <div className="mt-3 rounded-lg border border-border p-3" data-testid="rider-count-block">
              <Label className="text-xs">Estimated yearly riders in your region</Label>
              <div className="flex gap-2 mt-1.5">
                <Input type="number" min="0" value={riders} onChange={(e) => setRiders(e.target.value)} placeholder="4000" data-testid="rider-count-input" />
                <Button variant="outline" onClick={saveRiders} data-testid="rider-count-save">Save</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2" data-testid="example-cost-note">
                Example cost: about {(9900 / ridersNum).toFixed(1)}¢ per rider (based on {ridersNum.toLocaleString()} riders per year).
              </p>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
